import styled from '@emotion/styled'
import { Fragment, useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { font } from '../../styles'
import { padding } from '../../styles/size'

interface Choice {
   label: string | React.ReactNode
   render: React.ReactNode
}

interface MultiChoiceFormContainerProps {
   choices: Choice[]
   defaultSelected?: number
   labeWidth?: number
   onChange?: (index: number) => void
   className?: string
}

const MultiChoiceFormContainer: React.FC<MultiChoiceFormContainerProps> = ({
   choices,
   defaultSelected,
   onChange,
   labeWidth,
   className,
}) => {
   const [selected, setSelected] = useState<number>(defaultSelected || 0)
   const activeComponentRef = useRef<HTMLDivElement>(null)

   const handleChange = (index: number) => {
      setSelected(index)
      onChange && onChange(index)
   }

   useEffect(() => {
      if (defaultSelected !== undefined) {
         setSelected(defaultSelected)
      }
   }, [defaultSelected])

   return (
      <Holder data-testid="multi-choice-form-container" className={className}>
         <div className="control-buttons-container">
            {choices.map((choice, index) => (
               <span
                  className={'control-button ' + (selected === index ? 'selected' : '')}
                  key={index + 'control-button'}
                  onClick={() => handleChange(index)}
                  style={{ width: (labeWidth || 100) + 'px' }}
               >
                  {choice.label}
               </span>
            ))}
            <motion.span
               className="active-background"
               animate={{ translateX: selected * 100 + '%' }}
               transition={{ duration: 0.5, type: 'spring', stiffness: 100, damping: 12 }}
               style={{ width: 100 / choices.length + '%' }}
            />
         </div>
         <motion.div
            className="active-form-container"
            animate={{ height: 'auto' }}
            initial={{
               height: activeComponentRef.current?.getBoundingClientRect().height,
            }}
            transition={{ duration: 0.5, type: 'spring', stiffness: 100, damping: 12 }}
            key={selected + 'active-form-container'}
            ref={activeComponentRef}
         >
            {choices.map((choice, index) => {
               return (
                  <Fragment key={index}>
                     {selected === index && <div className="form-component">{choice.render}</div>}
                  </Fragment>
               )
            })}
         </motion.div>
      </Holder>
   )
}

const Holder = styled.div`
   --background-color: #f4f4f4;

   display: flex;
   flex-direction: column;
   align-items: center;
   background-color: var(--background-color);
   gap: 0.6em;
   width: 100%;
   border-radius: 1.3em;
   overflow: hidden;
   box-sizing: border-box;
   width: 100%;
   padding-top: ${padding.block};

   .control-buttons-container {
      position: relative;
      display: flex;
      flex-direction: row;
      justify-content: space-around;
      align-items: center;
      isolation: isolate;
      height: 2.5em;
      .control-button {
         color: #696969;
         ${font.bold};
         cursor: pointer;
         width: max-content;
         margin-inline: 2%;
         text-align: center;
         z-index: 1;
         transition: color 0.5s;
         white-space: nowrap;
         /* prevent text selection */
         user-select: none;

         &.selected {
            color: #fff;
         }
      }

      .active-background {
         position: absolute;
         top: 0px;
         left: 0;
         background-color: #6b6b6b;
         border-radius: 6em;
         height: 100%;
         mix-blend-mode: difference;
         z-index: -1;
      }
   }

   .active-form-container {
      width: 100%;
      .form-component {
         padding: ${padding.block} ${padding.inline};
         width: 100%;
         box-sizing: border-box;
      }
   }
`

export default MultiChoiceFormContainer
